"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { VALUES, TEAM } from "@/lib/data";

export default function About() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&display=swap');
        html { scroll-behavior: smooth; }
      `}</style>

      <main className="bg-[#fdfbf8] text-[#1a1a1a] font-['Cormorant_Garamond',Georgia,serif] min-h-screen">

        {/* HERO */}
        <section className="relative w-full h-[60vh] flex items-center justify-center overflow-hidden pt-20">
          <img
            src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=2070&auto=format&fit=crop"
            alt="Hero Background"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="pt-20 absolute inset-0 bg-black/40" />
          <div className="relative z-10 text-center text-white px-6">
            <h1 className="italic text-6xl md:text-8xl font-light">
              The Modish Story
            </h1>
            <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.4em] mb-4 opacity-90">
              Crafting Experiences
            </p>
          </div>
        </section>

        {/* ABOUT */}
        <section className="pt-10 py-12 md:py-16 px-6 md:px-10 max-w-[1040px] mx-auto">

          <div className="text-center mb-16 md:mb-20">
            <h1 className="italic font-light text-[clamp(5rem,5vw,4.5rem)] leading-[1.1]">
              We are <span className="text-[#b8955a]">Modish Kreations</span>
            </h1>
          </div>

          <div className="grid lg:grid-cols-12 gap-y-14 md:gap-y-16 gap-x-10 md:gap-x-14 items-start">

            {/* IMAGE */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="w-full h-[480px] border border-[#e8e2d9] overflow-hidden">
                  <img
                    src="/owner2.png"
                    alt="Founder"
                    className="w-full h-full object-cover"
                  />
                  <p className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-center py-2">
                    Ms. Laxmi Bhadauria
                  </p>
                </div>
                <div className="absolute -bottom-5 -right-5 w-full h-full border border-[#e8e2d9] -z-10"></div>
              </div>
            </div>

            {/* TOP RIGHT */}
            <div className="lg:col-span-7 max-w-[600px]">
              <p className="text-[1.05rem] leading-[1.85] text-[#2a2a2a] font-light mb-5 text-justify">
                <strong className="font-bold text-[#C7AB5F] text-[2rem]">Modish Kreations</strong> is a renowned brand in bespoke tailoring and was started by <strong className="font-bold">Ms. Laxmi Bhadauria</strong> in 2019 to bring back elegance and uniqueness to NCR, India. A heritage craftsmanship, seamlessly merged with modern sophistication, the brand is a mark of accuracy, creativity, and classical elegance.
              </p>
              <p className="text-[1.05rem] leading-[1.85] text-[#2a2a2a] font-light mb-5 text-justify">
                In Modish Kreations, we know that real luxury is in the details—where customization and craftsmanship intersect. Each piece starts with a detailed consultation and then a multi-step measuring process that guarantees a perfect fit tailored to each individual.
              </p>
              <p className="text-[1.05rem] leading-[1.9] text-[#2a2a2a] font-light mb-5 text-justify">
                Our practice balances premium materials with skilled artistry, resulting in garments that reflect confidence, comfort, and individuality. Every piece is carefully hand-cut and tailored by experienced craftsmen, ensuring it not only fits flawlessly but also maintains its character over time.
              </p>
              <p className="text-[1.05rem] leading-[1.9] text-[#2a2a2a] font-light text-justify">
                Built on trust, precision, and attention to detail, <strong className="font-bold text-[#051173]">Modish Kreations</strong> has become a preferred destination for those who value bespoke luxury and a highly personalized experience.
              </p>
            </div>
          </div>
        </section>

        <div className="border-t border-[#ede8e0]" />

        {/* STATS */}
        <section className="bg-[#faf8f5] py-16 border-y border-[#ede8e0]">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3">
              {[["120+", "Designs"], ["10+", "Years"], ["1000+", "Clients"]].map(([num, label], index) => (
                <div
                  key={label}
                  className={`
            flex flex-col items-center justify-center py-12 px-4 text-center
            md:border-r last:md:border-r-0 border-[#ede8e0]
            border-b md:border-b-0 last:border-b-0
          `}
                >
                  <h2 className="font-['Cormorant_Garamond'] italic text-[4rem] md:text-[5rem] leading-none text-[#b8955a] mb-6 font-light">
                    {num}
                  </h2>
                  <div className="w-8 h-[1px] bg-[#b8955a] mb-6 opacity-40"></div>

                  <p className="font-['DM_Mono'] text-[11px] tracking-[0.3em] uppercase text-black font-medium">
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* VALUES */}
        <section className="py-12 md:py-16 px-6 md:px-10 border-t border-[#ede8e0]">
          <div className="max-w-[1040px] mx-auto">
            <h2 className="italic font-light text-[3rem] mb-12 md:mb-14">
              Three things we never compromise on.
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#ede8e0]">
              {VALUES.map(({ num, title, desc }) => (
                <div key={num} className="bg-[#fdfbf8] p-8 md:p-10">
                  <p className="font-['DM_Mono'] text-[9px] text-[#b8955a] tracking-[0.22em] mb-4">
                    {num}
                  </p>
                  <h3 className="italic text-[1.3rem] mb-3">{title}</h3>
                  <p className="font-['DM_Mono'] text-[12px] text-black leading-[1.8]">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TEAM */}
        <section className="py-12 md:py-16 px-6 md:px-10 bg-[#faf8f5] border-t border-[#ede8e0]">
          <div className="max-w-[1040px] mx-auto">
            <h2 className="italic font-light text-[3rem] mb-12 md:mb-14">
              Small team. Serious craft.
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {TEAM.map((member) => (
                <div
                  key={member.id}
                  className="bg-white border border-[#ede8e0] p-5 transition-all hover:border-[#b8955a] hover:-translate-y-1 group"
                >
                  {/* Image Container - Isse image professional lagegi */}
                  <div className="relative w-full aspect-[4/5] mb-5 overflow-hidden bg-[#f0ebe3]">
                    {member.image ? (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                      />
                    ) : (
                      /* Fallback agar image na ho toh wahi initial dikhega */
                      <div className="w-full h-full flex items-center justify-center font-['DM_Mono'] text-2xl text-[black]">
                        {member.initial}
                      </div>
                    )}

                    {/* Subtle Overlay on Hover */}
                    <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>

                  <h3 className="italic text-[1.2rem] mb-1 text-[#121826]">
                    {member.name}
                  </h3>

                  <p className="font-['DM_Mono'] text-[9px] uppercase tracking-[0.14em] text-[#b8955a] mb-3">
                    {member.role}
                  </p>

                  <p className="font-['DM_Mono'] text-[10px] text-gray-600 leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
}